import React, { useEffect, useState } from "react";
import { Button, Col, Form, InputGroup, Row } from "react-bootstrap";
import {
  FaPlus,
  FaTrashAlt,
  FaStore,
  FaPhoneAlt,
  FaUniversity,
  FaRegClock,
  FaCog,
} from "react-icons/fa";
import {
  useCreateOutletMutation,
  useUploadOutletPhotoMutation,
} from "../slices/OutletSlice";
import { useGetAllAdminsQuery } from "../slices/AdminSilce";
import FieldWarning from "../components/FieldWarning";
import { isValidGstNumber } from "../utils/gstValidation";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { setCredentials } from "../slices/authSlice";
import imageCompression from "browser-image-compression";
import {
  cardStyle,
  labelStyle,
  Req,
  SectionCard,
  YesNoRadios,
} from "../components/OutletFormKit";

const CreateOutletScreen = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  // An existing admin of the company can be assigned to the new outlet.
  // Optional: the company's first outlet has no admins yet, so one is
  // created afterwards from the All Admins page.
  const [assignedAdminId, setAssignedAdminId] = useState("");
  const { data: companyAdmins } = useGetAllAdminsQuery({
    token: userInfo?.token,
  });
  const assignableAdmins = (
    Array.isArray(companyAdmins) ? companyAdmins : []
  ).filter((admin) => admin.adminType === "Admin");
  // const [outletId, setOutletId] = useState("");
  const [outletGroup, setOutletGroup] = useState("");
  // const [companyId, setCompanyId] = useState();
  const [seperateGst, setSeperateGst] = useState(true);
  const [outletCategory, setOutletCategory] = useState("");
  const [outletName, setOutletName] = useState("");
  const [outletAddress, setOutletAddress] = useState("");
  const [outletPincode, setOutletPincode] = useState("");
  const [outletState, setOutletState] = useState("Select State");
  const [outletContact, setOutletContact] = useState(
    userInfo?.mobileNumber.toString()
  );
  const [addedBy, setAddedBy] = useState("");
  const [operationStatus, setOperationStatus] = useState(true);
  const [openingHour, setOpeningHour] = useState("");
  const [closingHour, setClosingHour] = useState("");
  const [openingMinute, setOpeningMinute] = useState("");
  const [closingMinute, setClosingMinute] = useState("");
  const [openedDays, setOpenedDays] = useState([]);
  const [file, setFile] = useState(null);
  const [fileSizeError, setFileSizeError] = useState(false);
  const [fileInvalidError, setFileInvalidError] = useState(false);
  const [currentlyOpen, setCurrentlyOpen] = useState(true);
  const [restrictedOutlet, setRestrictedOutlet] = useState(false);
  const [wholeSaleOutlet, setWholeSaleOutlet] = useState(false);
  const [seperatePlatformFee, setSeperatePlatformFee] = useState(false);
  const [platformFee, setPlatformFee] = useState("");
  const [outletBankDetails, setOutletBankDetails] = useState("");
  const [outletGstNumber, setOutletGstNumber] = useState("");
  const [outletEmails, setOutletEmails] = useState([""]);
  const [secondaryContact, setSecondaryContact] = useState([""]);

  const updateListItem = (setter, index, value) =>
    setter((prev) => prev.map((item, i) => (i === index ? value : item)));

  const addListItem = (setter) => setter((prev) => [...prev, ""]);

  const removeListItem = (setter, index) =>
    setter((prev) => {
      const updated = prev.filter((item, i) => i !== index);
      return updated.length ? updated : [""];
    });

  const hours = [
    "12 AM",
    "01 AM",
    "02 AM",
    "03 AM",
    "04 AM",
    "05 AM",
    "06 AM",
    "07 AM",
    "08 AM",
    "09 AM",
    "10 AM",
    "11 AM",
    "12 PM",
    "01 PM",
    "02 PM",
    "03 PM",
    "04 PM",
    "05 PM",
    "06 PM",
    "07 PM",
    "08 PM",
    "09 PM",
    "10 PM",
    "11 PM",
  ];

  // Field errors are shown once the user has tried to create the outlet,
  // and then update live as each field is fixed
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const [loginRequired, setLoginRequired] = useState(false);
  const [printOnlineOrder, setPrintOnlineOrder] = useState(false);
  const [printOutletOrder, setPrintOutletOrder] = useState(false);

  const outCat = [
    "Select Category",
    "Restaurant",
    "Bakery",
    "Food Truck",
    "Theatre",
    "Bars",
    "Hotel",
    "Corporate",
    "Transport",
    "Train",
  ];

  const availableStateNames = [
    "Select State",
    "Andhra Pradesh",
    "Karnataka",
    "Kerala",
    "Maharashtra",
    "Tamil Nadu",
    "Telangana",
  ];
  const daysOfWeek = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const [registerOutlet, { isLoading }] = useCreateOutletMutation();
  const [uploadPhoto, { isLoading: isPhotoUploading }] =
    useUploadOutletPhotoMutation();

  const handleCheckboxChange = (day) => {
    setOpenedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };
  // Every field problem, keyed by field. Required fields follow the
  // backend outlet model; the rest are format checks on optional fields
  const getFieldErrors = () => {
    const errors = {};
    if (!outletName?.trim()) errors.outletName = "Please enter outlet name";
    if (!outletCategory || outletCategory === "Select Category")
      errors.outletCategory = "Please select outlet category";
    if ((outletAddress || "").trim().length < 10)
      errors.outletAddress = "Address should be atleast 10 chars long";
    if (!/^\d{10}$/.test(String(outletContact || "")))
      errors.outletContact = "Contact number should be 10 digits";
    if (!/^\d{6}$/.test(String(outletPincode || "")))
      errors.outletPincode = "Pincode should be 6 digits";
    if (!outletState || outletState === "Select State")
      errors.outletState = "Please select state";

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const badEmailRows = outletEmails
      .map((email, index) =>
        email.trim() && !emailRegex.test(email.trim()) ? index : null
      )
      .filter((index) => index !== null);
    if (badEmailRows.length) errors.outletEmails = badEmailRows;

    const badMobileRows = secondaryContact
      .map((mobile, index) =>
        String(mobile).trim() && !/^\d{10,12}$/.test(String(mobile).trim())
          ? index
          : null
      )
      .filter((index) => index !== null);
    if (badMobileRows.length) errors.secondaryContact = badMobileRows;

    if (outletGstNumber.trim() && !isValidGstNumber(outletGstNumber))
      errors.outletGstNumber = "GST number should be exactly 15 characters";
    const fee = String(platformFee ?? "").trim();
    if (fee !== "" && Number(fee) > 100)
      errors.platformFee = "Platform fee should be between 0 and 100";

    if (!openingHour || !openingMinute)
      errors.openingTime = "Please select opening hour and minutes";
    if (!closingHour || !closingMinute)
      errors.closingTime = "Please select closing hour and minutes";
    if (openedDays.length === 0)
      errors.openedDays = "Please select at least one opened day";
    return errors;
  };

  const fieldErrors = showFieldErrors ? getFieldErrors() : {};

  const verifyInputFields = () => {
    setShowFieldErrors(true);
    if (Object.keys(getFieldErrors()).length > 0) {
      toast.error("Please fix the fields marked in red");
      return false;
    }
    return true;
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (verifyInputFields())
      try {
        const res = await registerOutlet({
          outletGroup: outletGroup,
          separateGst: seperateGst,
          outletCategory: outletCategory,
          outletName: outletName,
          outletAddress: outletAddress,
          outletPincode: outletPincode,
          outletState: outletState,
          outletContact: Number(outletContact),
          addedBy: userInfo?.adminName,
          operationStatus: operationStatus,
          currentlyOpen: currentlyOpen,
          openingHour: Number(
            (openingHour === "12 AM"
              ? 0
              : openingHour?.includes("PM")
              ? Number(openingHour.split(" ")[0]) +
                (openingHour === "12 PM" ? 0 : 12)
              : Number(openingHour.split(" ")[0])) +
              "." +
              openingMinute
          ),
          closingHour: Number(
            (closingHour === "12 AM"
              ? 0
              : closingHour?.includes("PM")
              ? Number(closingHour.split(" ")[0]) +
                (closingHour === "12 PM" ? 0 : 12)
              : Number(closingHour.split(" ")[0])) +
              "." +
              closingMinute
          ),
          openedDays: openedDays,
          operationStatus: operationStatus,
          loginRequired: loginRequired,
          printOnlineOrder: printOnlineOrder,
          printOutletOrder: printOutletOrder,
          restrictedOutlet: restrictedOutlet,
          wholeSaleOutlet: wholeSaleOutlet,
          separatePlaformFee: seperatePlatformFee,
          platformFee: platformFee,
          outletBankDetails: outletBankDetails,
          outletGstNumber: outletGstNumber,
          outletEmails: outletEmails
            .map((email) => email.trim())
            .filter((email) => email),
          secondaryContact: secondaryContact
            .map((mobile) => String(mobile).trim())
            .filter((mobile) => mobile)
            .map(Number),
          ...(assignedAdminId && { adminId: assignedAdminId }),
          token: userInfo.token,
        }).unwrap();
        if (res && file) {
          let formData = new FormData();
          formData.append("image", file);
          formData.append("imageKey", `${res._id}`);
          await uploadPhoto({
            token: userInfo.token,
            payload: formData,
          }).unwrap();
        }
        if (res) {
          toast.success(
            res.outletAdmin
              ? `Outlet registered and assigned to ${res.outletAdmin.adminName}.`
              : "Outlet registered. Create or assign its admin from the All Admins page or the outlet's Add Admin option."
          );
          // Owners work across every outlet; add the new one so it can be
          // selected without logging in again.
          const { outletAdmin, ...newOutlet } = res;
          dispatch(
            setCredentials({
              ...userInfo,
              outletId: [...(userInfo?.outletId || []), newOutlet],
              selectedOutletId: userInfo?.selectedOutletId || newOutlet._id,
            })
          );
          setAssignedAdminId("");
          // setOutletId("");
          setOutletGroup("");
          // setCompanyId("");
          setSeperateGst(true);
          setOutletCategory("");
          setOutletName("");
          setOutletAddress("");
          setOutletPincode("");
          setOutletState("Select State");
          setOutletContact("");
          setAddedBy("");
          setOutletBankDetails("");
          setOutletGstNumber("");
          setOutletEmails([""]);
          setSecondaryContact([""]);
          setOperationStatus(true);
          setCurrentlyOpen(true);
          setOpeningHour("");
          setOpeningMinute("");
          setClosingHour("");
          setClosingMinute("");
          setOpenedDays([]);
          setShowFieldErrors(false);
        }
      } catch (e) {
        toast.error(
          e?.data?.error ||
            e?.data?.message ||
            "Outlet could not be created, please try again"
        );
      }
  };

  const compressImage = async (selectedImage) => {
    const file = selectedImage;
    if (file) {
      try {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 280,
          useWebWorker: true,
          initialQuality: 1,
        };

        console.log("Un-Compressed file:", file);
        const compressedFile = await imageCompression(file, options);
        // setFile(compressedFile);
        console.log("first Compressed file:", compressedFile);
        const secondOptions = {
          maxSizeMB: 1, // maximum size in MB
          useWebWorker: true, // use web worker for performance
          initialQuality: 0.9, // initial quality setting
        };

        // Second compression step
        const secondCompressedFile = await imageCompression(
          compressedFile,
          secondOptions
        );
        console.log("Second Compressed file:", secondCompressedFile);
        setFile(secondCompressedFile);

        // // You can now upload the compressed file to your server
        // uploadImage(compressedFile);
      } catch (error) {
        console.error("Error processing image:", error);
      }
    }
  };

  const handleFileChange = async (e) => {
    const selectedFile = e?.target?.files[0];
    const dotIndex = selectedFile?.name?.lastIndexOf(".");
    const fileExtension = selectedFile?.name?.slice(dotIndex + 1).toLowerCase();
    let errorPresent = false;
    if (
      !(
        fileExtension === "jpeg" ||
        fileExtension === "jpg" ||
        fileExtension === "png" ||
        fileExtension === "tiff" ||
        fileExtension === "svg" ||
        fileExtension === "jfif"
      )
    ) {
      errorPresent = true;
      setFileInvalidError(true);
      setFile(null);
    } else if (selectedFile?.size / 1000 > 5120) {
      setFileSizeError(true);
      setFile(null);
    } else if (!errorPresent && selectedFile?.size / 1000 <= 5120) {
      setFileSizeError(false);
      setFileInvalidError(false);
      // setFile(selectedFile);
      await compressImage(selectedFile);
    }
  };

  const minuteOptions = Array.from({ length: 60 }, (_, i) =>
    i.toString().padStart(2, "0")
  );

  return (
    <div
      style={{
        padding: "0.5rem 0.6rem",
        maxWidth: "960px",
        margin: "0 auto",
      }}
    >
      {/* Page header */}
      <div
        style={{
          ...cardStyle,
          padding: "1rem 1.1rem",
          marginBottom: "0.9rem",
        }}
      >
        <h5 className="mb-1">
          <strong style={{ color: "#1d5f91" }}>Create Outlet</strong>
        </h5>
        <small className="text-muted">
          Fill in the details below to register a new outlet
        </small>
      </div>

      <Form>
        {/* Basic details */}
        <SectionCard
          icon={<FaStore size={15} />}
          title="Basic Details"
          subtitle="Name, category and address of the outlet"
        >
          <Row style={{ rowGap: "1rem" }}>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>
                Outlet Name
                <Req />
              </Form.Label>
              <Form.Control
                type="text"
                value={outletName}
                onChange={(e) => setOutletName(e.target.value)}
                placeholder="Enter outlet name"
              />
              {fieldErrors.outletName && (
                <FieldWarning text={fieldErrors.outletName} />
              )}
            </Col>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>
                Outlet Category
                <Req />
              </Form.Label>
              <Form.Select
                value={outletCategory}
                onChange={(e) => setOutletCategory(e.target.value)}
              >
                {outCat?.map((e) => (
                  <option key={e} value={e}>
                    {e}
                  </option>
                ))}
              </Form.Select>
              {fieldErrors.outletCategory && (
                <FieldWarning text={fieldErrors.outletCategory} />
              )}
            </Col>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>Outlet Group</Form.Label>
              <Form.Control
                type="text"
                value={outletGroup}
                onChange={(e) => setOutletGroup(e.target.value)}
                placeholder="Enter Outlet Group"
              />
            </Col>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>Outlet Image</Form.Label>
              <Form.Control
                type="file"
                accept=".jpeg,.jpg,.png,.tiff,.svg,.jfif"
                onChange={handleFileChange}
              />
              {file && !fileSizeError && !fileInvalidError && (
                <small style={{ color: "#1e7e34" }}>
                  Image selected and ready to upload
                </small>
              )}
              {fileSizeError && (
                <FieldWarning text="Image size should be less than 5 MB, please select a different image" />
              )}
              {fileInvalidError && (
                <FieldWarning text="Only jpeg, jpg, png, tiff and svg formats are supported" />
              )}
            </Col>
            <Col xs={12}>
              <Form.Label style={labelStyle}>
                Outlet Address
                <Req />
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={outletAddress}
                onChange={(e) => setOutletAddress(e.target.value)}
                placeholder={
                  "SRI POOJA SNACKS\nNO:8/10, KRISHNARAYAR THEPPAM WEST STREET\nMANI NAGARAM ADURAI -62506\nSTIN: 33BJAPS9693D12"
                }
                style={{ resize: "vertical", whiteSpace: "pre-wrap" }}
              />
              {fieldErrors.outletAddress && (
                <FieldWarning text={fieldErrors.outletAddress} />
              )}
            </Col>
          </Row>
        </SectionCard>

        {/* Contact details */}
        <SectionCard
          icon={<FaPhoneAlt size={14} />}
          title="Contact & Location"
          subtitle="Phone numbers, emails and location details"
        >
          <Row style={{ rowGap: "1rem" }}>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>
                Outlet Contact
                <Req />
              </Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                value={outletContact}
                onChange={(e) => {
                  const val = e.currentTarget.value.replace(/[^0-9]/g, "");
                  setOutletContact(val);
                }}
                placeholder="Enter contact number"
              />
              {fieldErrors.outletContact && (
                <FieldWarning text={fieldErrors.outletContact} />
              )}
            </Col>
            <Col xs={6} md={3}>
              <Form.Label style={labelStyle}>
                Pincode
                <Req />
              </Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                value={outletPincode}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, "");
                  setOutletPincode(val);
                }}
                placeholder="Enter Pincode"
              />
              {fieldErrors.outletPincode && (
                <FieldWarning text={fieldErrors.outletPincode} />
              )}
            </Col>
            <Col xs={6} md={3}>
              <Form.Label style={labelStyle}>
                State
                <Req />
              </Form.Label>
              <Form.Select
                value={outletState}
                onChange={(e) => setOutletState(e.target.value)}
              >
                {availableStateNames?.map((e) => (
                  <option key={e} value={e}>
                    {e}
                  </option>
                ))}
              </Form.Select>
              {fieldErrors.outletState && (
                <FieldWarning text={fieldErrors.outletState} />
              )}
            </Col>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>Outlet Emails</Form.Label>
              {outletEmails.map((email, index) => (
                <div className="mb-2" key={index}>
                  <InputGroup>
                    <Form.Control
                      type="email"
                      placeholder="Enter email"
                      value={email}
                      onChange={(e) =>
                        updateListItem(setOutletEmails, index, e.target.value)
                      }
                    />
                    <Button
                      variant="outline-danger"
                      onClick={() => removeListItem(setOutletEmails, index)}
                    >
                      <FaTrashAlt />
                    </Button>
                  </InputGroup>
                  {fieldErrors.outletEmails?.includes(index) && (
                    <FieldWarning text="Please enter a valid email" />
                  )}
                </div>
              ))}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => addListItem(setOutletEmails)}
              >
                <FaPlus style={{ marginBottom: "2px" }} /> Add Email
              </Button>
            </Col>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>Secondary Mobiles</Form.Label>
              {secondaryContact.map((mobile, index) => (
                <div className="mb-2" key={index}>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      placeholder="Enter mobile number"
                      value={mobile}
                      onChange={(e) =>
                        updateListItem(
                          setSecondaryContact,
                          index,
                          e.target.value
                        )
                      }
                    />
                    <Button
                      variant="outline-danger"
                      onClick={() => removeListItem(setSecondaryContact, index)}
                    >
                      <FaTrashAlt />
                    </Button>
                  </InputGroup>
                  {fieldErrors.secondaryContact?.includes(index) && (
                    <FieldWarning text="Mobile number should be 10 to 12 digits" />
                  )}
                </div>
              ))}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => addListItem(setSecondaryContact)}
              >
                <FaPlus style={{ marginBottom: "2px" }} /> Add Mobile
              </Button>
            </Col>
          </Row>
        </SectionCard>

        {/* Billing details */}
        <SectionCard
          icon={<FaUniversity size={15} />}
          title="Billing & Bank"
          subtitle="GST, platform fee and bank account details"
        >
          <Row style={{ rowGap: "1rem" }}>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>GST Number</Form.Label>
              <Form.Control
                type="text"
                value={outletGstNumber}
                onChange={(e) =>
                  setOutletGstNumber(e.target.value.toUpperCase())
                }
                placeholder="Enter GST number"
              />
              {fieldErrors.outletGstNumber && (
                <FieldWarning text={fieldErrors.outletGstNumber} />
              )}
            </Col>
            <Col xs={12} sm={6} md={6}>
              <Form.Label style={labelStyle}>
                Seperate GST ?
                <Req />
              </Form.Label>
              <YesNoRadios
                name="platRadio"
                value={seperateGst}
                onChange={setSeperateGst}
              />
            </Col>
            {/* <Col xs={12} sm={6} md={6}>
              <Form.Label style={labelStyle}>
                Seperate Platform Fee ?
              </Form.Label>
              <YesNoRadios
                name="platformRadio"
                value={seperatePlatformFee}
                onChange={setSeperatePlatformFee}
              />
            </Col>
            <Col xs={12} sm={6} md={6}>
              <Form.Label style={labelStyle}>Platform Fee %</Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                value={platformFee}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, "");
                  setPlatformFee(value);
                }}
                placeholder="Enter Platformfee"
              />
              {fieldErrors.platformFee && (
                <FieldWarning text={fieldErrors.platformFee} />
              )}
            </Col> */}
            <Col xs={12}>
              <Form.Label style={labelStyle}>Bank Account Details</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={outletBankDetails}
                onChange={(e) => setOutletBankDetails(e.target.value)}
                placeholder={
                  "Bank Name : XYZ Bank\nAccount Number : 23423435\nAccount Name : David\nIFSC code : 43534543"
                }
                style={{ resize: "vertical", whiteSpace: "pre-wrap" }}
              />
            </Col>
          </Row>
        </SectionCard>

        {/* Operating hours */}
        <SectionCard
          icon={<FaRegClock size={15} />}
          title="Operating Hours"
          subtitle="When the outlet is open for business"
        >
          <Row style={{ rowGap: "1rem" }}>
            <Col xs={12} sm={6}>
              <Form.Label style={labelStyle}>
                In Operation Now ?
                <Req />
              </Form.Label>
              <YesNoRadios
                name="operationStatus"
                value={operationStatus}
                onChange={setOperationStatus}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Form.Label style={labelStyle}>Currently Open ?</Form.Label>
              <YesNoRadios
                name="currentlyOpen"
                value={currentlyOpen}
                onChange={setCurrentlyOpen}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Form.Label style={labelStyle}>
                Opening Time (HH:MM)
                <Req />
              </Form.Label>
              <div className="d-flex" style={{ gap: "0.5rem" }}>
                <Form.Select
                  value={openingHour}
                  onChange={(e) => setOpeningHour(e.target.value)}
                >
                  <option value="">Select Hour</option>
                  {hours.map((hour) => (
                    <option key={hour} value={hour}>
                      {hour}
                    </option>
                  ))}
                </Form.Select>
                <Form.Select
                  value={openingMinute}
                  onChange={(e) => setOpeningMinute(e.target.value)}
                >
                  <option value="">Select Minutes</option>
                  {minuteOptions.map((mins) => (
                    <option key={mins} value={mins}>
                      {mins}
                    </option>
                  ))}
                </Form.Select>
              </div>
              {fieldErrors.openingTime && (
                <FieldWarning text={fieldErrors.openingTime} />
              )}
            </Col>
            <Col xs={12} sm={6}>
              <Form.Label style={labelStyle}>
                Closing Time (HH:MM)
                <Req />
              </Form.Label>
              <div className="d-flex" style={{ gap: "0.5rem" }}>
                <Form.Select
                  value={closingHour}
                  onChange={(e) => setClosingHour(e.target.value)}
                >
                  <option value="">Select Hour</option>
                  {hours.map((hour) => (
                    <option key={hour} value={hour}>
                      {hour}
                    </option>
                  ))}
                </Form.Select>
                <Form.Select
                  value={closingMinute}
                  onChange={(e) => setClosingMinute(e.target.value)}
                >
                  <option value="">Select Minutes</option>
                  {minuteOptions.map((mins) => (
                    <option key={mins} value={mins}>
                      {mins}
                    </option>
                  ))}
                </Form.Select>
              </div>
              {fieldErrors.closingTime && (
                <FieldWarning text={fieldErrors.closingTime} />
              )}
            </Col>
            <Col xs={12}>
              <Form.Label style={labelStyle}>
                Opened Days
                <Req />
              </Form.Label>
              <Row>
                {daysOfWeek.map((day) => (
                  <Col key={day} xs={6} sm={4} md={3} className="mb-2">
                    <Form.Check
                      type="checkbox"
                      id={`day-${day}`}
                      label={day}
                      checked={openedDays.includes(day)}
                      onChange={() => handleCheckboxChange(day)}
                    />
                  </Col>
                ))}
              </Row>
              {fieldErrors.openedDays && (
                <FieldWarning text={fieldErrors.openedDays} />
              )}
            </Col>
          </Row>
        </SectionCard>

        {/* Preferences */}
        <SectionCard
          icon={<FaCog size={15} />}
          title="Preferences"
          subtitle="Access and printing preferences"
        >
          <Row style={{ rowGap: "1rem" }}>
            <Col xs={12} sm={6}>
              <Form.Label style={labelStyle}>Restricted Outlet ?</Form.Label>
              <YesNoRadios
                name="restrictedOutlet"
                value={restrictedOutlet}
                onChange={setRestrictedOutlet}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Form.Label style={labelStyle}>Wholesale Outlet ?</Form.Label>
              <YesNoRadios
                name="wholeSaleOutlet"
                value={wholeSaleOutlet}
                onChange={setWholeSaleOutlet}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Form.Label style={labelStyle}>Login Required ?</Form.Label>
              <YesNoRadios
                name="loginRequired"
                value={loginRequired}
                onChange={setLoginRequired}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Form.Label style={labelStyle}>
                Automatically Print Online Orders ?
              </Form.Label>
              <YesNoRadios
                name="printOnlineOrder"
                value={printOnlineOrder}
                onChange={setPrintOnlineOrder}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Form.Label style={labelStyle}>
                Automatically Print Outlet Orders ?
              </Form.Label>
              <YesNoRadios
                name="printOutletOrder"
                value={printOutletOrder}
                onChange={setPrintOutletOrder}
              />
            </Col>
          </Row>
        </SectionCard>

        {/* Outlet admin */}
        <SectionCard
          icon={<FaStore size={15} />}
          title="Outlet Admin"
          subtitle="Optional: assign an existing admin, who takes orders at this outlet. Owners can't place orders."
        >
          <Row style={{ rowGap: "1rem" }}>
            <Col xs={12} sm={6}>
              <Form.Label style={labelStyle}>Assign Admin</Form.Label>
              {assignableAdmins.length > 0 ? (
                <Form.Select
                  value={assignedAdminId}
                  onChange={(e) => setAssignedAdminId(e.target.value)}
                >
                  <option value="">Do not assign now</option>
                  {assignableAdmins.map((admin) => (
                    <option key={admin._id} value={admin._id}>
                      {admin.adminName} ({admin.mobileNumber})
                    </option>
                  ))}
                </Form.Select>
              ) : (
                <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                  No admins yet. After creating this outlet, create its admin
                  from the All Admins page.
                </div>
              )}
            </Col>
          </Row>
        </SectionCard>

        {/* Submit */}
        <div
          style={{
            ...cardStyle,
            padding: "0.9rem 1rem",
            marginBottom: "1.5rem",
          }}
        >
          <Row className="justify-content-center">
            <Col xs={12} sm={8} md={5} className="d-grid">
              <Button
                className={`${userInfo?.themeColor}ThemeHeaderTop`}
                style={{
                  borderRadius: "8px",
                  fontWeight: "600",
                  padding: "0.6rem",
                }}
                onClick={submitHandler}
                disabled={isLoading || isPhotoUploading}
              >
                {isLoading || isPhotoUploading
                  ? "Submitting..."
                  : "Create Outlet"}
              </Button>
            </Col>
          </Row>
        </div>
      </Form>
    </div>
  );
};

export default CreateOutletScreen;
